import { useEffect, useState } from 'react';
import { ColumnData, HandleLoginMap } from '../interface/interface';
import instance from '../config/axios';
import Loading from '../components/Loading';
import { useAuth } from 'react-oidc-context';
import moment from 'moment';
import { Table } from '../components/Table';
import { PaginationState, RowSelectionState } from '@tanstack/react-table';
import { stringify } from 'qs';
import { useSocket } from '../hooks/useSocket';
import { ArchiveTokenIcon } from '../assets/svgImages.tsx';
import showToast from '../components/ToastComponent.tsx';
import axios from '../config/axios';
import { AxiosError } from 'axios';

const ListErxToken = () => {
  const [state, setState] = useState<HandleLoginMap<'ResultMap'>>({
    showResult: false,
    data: [],
    error: null,
  });

  const { user } = useAuth();
  const [, , , refreshTokens] = useSocket(user?.access_token as string);

  const [isFetching, setIsFetching] = useState(false);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [forceUpdate, setForceUpdateCounter] = useState(0);

  const fetchTokens = async () => {
    try {
      setIsFetching(true);
      const token = user?.access_token;
      const response = await instance.get(
        `api/v1/tokens?${stringify({ page: pagination.pageIndex, limit: pagination.pageSize })}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setTotalRecords(response.data.total);

      setState({
        ...state,
        showResult: true,
        data: response.data.data,
      });

      setIsFetching(false);
    } catch (error: any) {
      setState({
        ...state,
        error: error.error,
      });
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, [refreshTokens, pagination, forceUpdate]);

  if (isFetching) {
    return <Loading />;
  }

  // Table Specific Props
  const columns: Array<ColumnData> = [
    {
      key: 'id',
      header: () => 'Select',
      cell: ({ row, getValue }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected() || rowSelection?.[getValue()]}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    },
    {
      key: 'token',
      header: 'eRX Token',
      cell: (info) => info.getValue(),
    },
    {
      key: 'createdDate',
      header: 'Created Date',
      cell: (info) => {
        const date = moment(new Date(info.getValue()));
        return date.isValid() ? moment(date).format('DD/MM/YY hh:mm a') : '-';
      },
    },
    {
      key: 'reason',
      header: 'Error Info',
      cell: (info) => info.getValue(),
    },
  ];

  async function handleArchival() {
    const tokensToArchive = Object.keys(rowSelection ?? {})?.flatMap((rs) => {
      if (rowSelection?.[rs]) return [state?.data?.[Number(rs)]?.id];
    });

    if (!tokensToArchive?.length) return;

    try {
      setIsFetching(true);
      const response = await axios({
        url: '/api/v1/tokens/archive',
        method: 'PUT',
        data: { tokenRefToArchive: tokensToArchive },
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      });
      setRowSelection({});
      setForceUpdateCounter((p) => p + 1);
      showToast({ type: 'success', message: response.data.message });
    } catch (e) {
      showToast({ message: 'Unable to archive token', type: 'info' });
      console.log(
        'D> Unable to archive token',
        (e as AxiosError)?.response?.data,
      );
    } finally {
      setIsFetching(false);
    }
  }

  async function reTriggerToken() {
    try {
      setIsFetching(true);
      await axios({
        url: '/api/v1/tokens/retrigger',
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      });
      showToast({
        type: 'success',
        message:
          'The re-trigger process has been initiated. Tokens will be updated shortly.',
      });
    } catch (e) {
      showToast({ message: 'Unable to Re-Trigger the process', type: 'info' });
    } finally {
      setIsFetching(false);
    }
  }

  return (
    <div className="flow-root">
      <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8 min-h-[600px]">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 relative token-table-wrapper">
          <div className="sm:flex-auto my-2 py-2 flex justify-between items-center mb-3">
            <h1 className="text-xl font-semibold leading-6 text-primary-blue-500">
              eRx Tokens
            </h1>
            <div className="flex">
              <button
                type="button"
                onClick={() => reTriggerToken()}
                className={
                  'ml-2 block rounded-md bg-secondary-green-600 px-10 py-3 text-center text-sm font-semibold text-white flex items-center'
                }
              >
                {/* <ArchiveTokenIcon className="w-5 h-5" /> */}
                <span className="">Rerun</span>
              </button>
              <button
                type="button"
                disabled={!Object.keys(rowSelection ?? {})?.length}
                onClick={() => handleArchival()}
                className={`ml-2 block rounded-md bg-secondary-pink-600 px-10 py-3 text-center text-sm font-semibold text-white flex items-center 
    ${!Object.keys(rowSelection ?? {})?.length ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <ArchiveTokenIcon className="w-5 h-5" />
                <span className="ml-3">Archive Selected</span>
              </button>
            </div>
          </div>
          {state?.data?.length ? (
            <Table
              columns={columns}
              data={state.data}
              enablePagination
              enableRowSelection
              pagintation={{
                state: pagination,
                setter: setPagination,
                totalRecords,
              }}
              rowSelection={{
                state: rowSelection ?? {},
                setter: setRowSelection,
              }}
            />
          ) : (
            <p className="text-base text-primary-blue-500 font-semibold text-center my-10">
              No Token data found!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListErxToken;
